import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useState, useEffect, useRef, type RefObject } from 'react'
import { useAuth } from '@/lib/auth'
import { loginApi, discoverSsoApi } from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'
import { toast } from 'sonner'
import { ArrowRightIcon, EyeIcon, EyeOffIcon, ZapIcon, CheckCircleIcon, ShieldCheckIcon, MailIcon, SmartphoneIcon } from 'lucide-react'
import { PhoneLoginPanel } from '@/components/PhoneLoginPanel'

// ─────────────────────────────────────────────────────────────
// Scroll-reveal hook
// ─────────────────────────────────────────────────────────────
function useInView(ref: RefObject<HTMLElement | null>, opts?: IntersectionObserverInit) {
  const [inView, setInView] = useState(false)
  const threshold = opts?.threshold ?? 0.1
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.unobserve(el)
        }
      },
      { threshold },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, threshold])
  return inView
}

function RevealSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref)
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out will-change-transform ${
        inView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      } ${className}`}
    >
      {children}
    </div>
  )
}

export const Route = createFileRoute('/login')({
  component: () => {
    const navigate = useNavigate()
    const { login } = useAuth()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [ssoData, setSsoData] = useState<{ sso_enabled: boolean; protocol?: string; redirect_url?: string } | null>(null)
    const [ssoChecking, setSsoChecking] = useState(false)
    const [showSso, setShowSso] = useState(false)
    const [mode, setMode] = useState<'email' | 'phone'>('email')
    const ssoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (ssoTimerRef.current) clearTimeout(ssoTimerRef.current)
      }
    }, [])

    // SSO discovery on email change
    const checkSso = async (emailValue: string) => {
      if (!emailValue.includes('@')) {
        setSsoData(null)
        return
      }
      setSsoChecking(true)
      try {
        const result = await discoverSsoApi(emailValue)
        setSsoData(result)
      } catch {
        setSsoData(null)
      } finally {
        setSsoChecking(false)
      }
    }

    const handleEmailChange = (value: string) => {
      setEmail(value)
      setShowSso(false)
      // Debounce SSO check
      if (ssoTimerRef.current) clearTimeout(ssoTimerRef.current)
      if (value.includes('@')) {
        ssoTimerRef.current = setTimeout(() => checkSso(value), 400)
      } else {
        setSsoData(null)
      }
    }

    const handleSsoLogin = () => {
      if (!ssoData?.redirect_url) return
      // Redirect to the SSO login endpoint
      const apiBase = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'
      window.location.href = `${apiBase}${ssoData.redirect_url}`
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      if (!email || !password) {
        toast.error('Please enter your email and password')
        return
      }

      setLoading(true)
      try {
        const data = await loginApi(email, password)

        // Check for SSO-redirect response
        if (data.sso_required) {
          // Discover the SSO config to get the redirect URL
          try {
            const discovered = await discoverSsoApi(email)
            if (discovered.sso_enabled) {
              setSsoData(discovered)
            } else {
              setSsoData({ sso_enabled: true, protocol: 'saml' })
            }
          } catch {
            setSsoData({ sso_enabled: true, protocol: 'saml' })
          }
          setShowSso(true)
          toast.info('This account uses SSO. Sign in with your company account.')
          setLoading(false)
          return
        }

        // Search for the JWT token across common nesting patterns.
        let token: string | undefined
        const sources: Record<string, unknown>[] = [data as Record<string, unknown>]
        const dataObj = data.data as Record<string, unknown> | undefined
        if (dataObj) sources.push(dataObj)
        for (const src of sources) {
          const val = src.token
          if (typeof val === 'string' && val.length > 0) {
            token = val
            break
          }
        }

        if (!token) {
          throw new Error('No token received from server')
        }

        // login() verifies the JWT signature, extracts user data, and persists to localStorage.
        // Pass the full response data as supplemental info (user_id, user_role, etc.)
        await login(token, data)

        toast.success('Welcome back!')
        navigate({ to: '/dashboard' })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred'
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    const handlePhoneAuthenticated = async (token: string, data: Record<string, unknown>) => {
      await login(token, data)
      toast.success('Welcome back!')
      navigate({ to: '/dashboard' })
    }

    return (
      <div className="relative flex min-h-svh flex-col bg-[#0B1120] overflow-hidden">
        {/* Subtle grid pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Decorative glow orbs */}
        <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />
        <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[300px] rounded-full bg-[#00E5A0]/5 blur-[80px]" />

        {/* Brand lockup */}
        <div className="relative z-10 flex items-center justify-center pt-24 sm:pt-28">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold shadow-lg shadow-[#00E5A0]/30">
              PP
            </div>
            <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
          </Link>
        </div>

        {/* Login form */}
        <div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-12 sm:pb-16">
          <RevealSection className="w-full max-w-md">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-black/50">
              {/* Mint accent bar */}
              <span
                aria-hidden="true"
                className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
                style={{ backgroundColor: BRAND_MINT }}
              />

              <div className="flex flex-col gap-2 text-center mb-8">
                <h1 className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl font-medium tracking-tight text-white">
                  Welcome back
                </h1>
                <p className="text-sm text-white/60">
                  Log in to manage your circles and payments
                </p>
              </div>

              {/* Auth method switcher */}
              <div className="grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-white/[0.04] p-1 mb-6">
                <button
                  type="button"
                  onClick={() => setMode('email')}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-colors ${
                    mode === 'email' ? 'bg-[#00E5A0] text-[#0B1120]' : 'text-white/50 hover:text-white/80'
                  }`}
                >
                  <MailIcon className="size-4" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setMode('phone')}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-colors ${
                    mode === 'phone' ? 'bg-[#00E5A0] text-[#0B1120]' : 'text-white/50 hover:text-white/80'
                  }`}
                >
                  <SmartphoneIcon className="size-4" />
                  Phone
                </button>
              </div>

              {mode === 'email' ? (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5" aria-label="Log in">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email" className="text-sm font-medium text-white/70">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    autoComplete="email"
                    className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
                  />
                  {/* SSO discovery indicator */}
                  {ssoChecking && (
                    <p className="text-xs text-white/30">Checking SSO availability…</p>
                  )}
                  {ssoData?.sso_enabled && !showSso && !ssoChecking && (
                    <div className="flex items-center gap-2 mt-1">
                      <ShieldCheckIcon className="size-3.5 text-[#00E5A0]" />
                      <p className="text-xs text-[#00E5A0]/80">
                        SSO available for your company.{' '}
                        <button type="button" onClick={() => setShowSso(true)} className="underline hover:text-[#00E5A0] font-medium">
                          Sign in with SSO
                        </button>
                      </p>
                    </div>
                  )}
                </div>

                {/* SSO login view */}
                {showSso && ssoData?.sso_enabled ? (
                  <>
                    <div className="flex flex-col gap-3 rounded-xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.06] p-4">
                      <div className="flex items-center gap-2">
                        <ShieldCheckIcon className="size-4 text-[#00E5A0]" />
                        <p className="text-sm font-medium text-[#00E5A0]">
                          Sign in with your company account
                        </p>
                      </div>
                      <p className="text-xs text-white/50">
                        You'll be redirected to your company's identity provider to sign in securely.
                      </p>
                    </div>

                    <Button
                      type="button"
                      onClick={handleSsoLogin}
                      className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 shadow-xl shadow-[#00E5A0]/25"
                    >
                      <span className="flex items-center gap-2">
                        <ShieldCheckIcon className="size-4" />
                        Sign in with SSO
                      </span>
                    </Button>

                    <button
                      type="button"
                      onClick={() => { setShowSso(false); setSsoData(null) }}
                      className="text-xs text-white/30 hover:text-white/50 text-center transition-colors"
                    >
                      Use password instead
                    </button>
                  </>
                ) : (
                  <>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium text-white/70">
                      Password
                    </Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-white/40 hover:text-[#00E5A0] transition-colors"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                      className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 mt-2 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="size-4 rounded-full border-2 border-[#0B1120]/30 border-t-[#0B1120] animate-spin" />
                      Signing in…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Log in
                      <ArrowRightIcon className="size-4" />
                    </span>
                  )}
                </Button>
                  </>
                )}
              </form>
              ) : (
                <PhoneLoginPanel onAuthenticated={handlePhoneAuthenticated} />
              )}

              {/* Dev test credentials */}
              {!import.meta.env.PROD && (
                <div className="mt-6 rounded-xl border border-[#00E5A0]/15 bg-[#00E5A0]/[0.04] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ZapIcon className="size-3.5 text-[#00E5A0]" />
                    <p className="text-xs font-medium text-[#00E5A0]/80">Test credentials</p>
                  </div>
                  <p className="text-xs text-white/40 font-mono">
                    admin1@nike.com / Pass123
                  </p>
                </div>
              )}

              {/* Footer link */}
              <p className="mt-6 text-center text-sm text-white/40">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="text-[#00E5A0] font-medium hover:text-[#00E5A0]/80 underline underline-offset-4 transition-colors"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </RevealSection>
        </div>

        {/* Bottom trust bar */}
        <div className="relative z-10 flex justify-center pb-8">
          <div className="flex items-center gap-4 text-xs text-white/30">
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              Employer-paid benefit
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              0% interest
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              100% anonymous
            </span>
          </div>
        </div>
      </div>
    )
  },
})
